import sumBy from 'lodash/sumBy';
import round from 'lodash/round';
import isBoolean from 'lodash/isBoolean';
import {
  Form,
  SelectInput,
  TextInput,
  Typography,
  PLATFORM_THEME_COLORS,
  AddressInput,
  TextAreaInput,
  DatePickerInput,
  CheckboxSelect,
  HoursInput,
} from '@thinknet-gmbh/platform-common-ui';
import { useEffect, useState } from 'react';
import { FormInstance } from 'antd';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useCurrentMemberContext } from 'src/contexts';
import { useGetGantts } from 'src/hooks/gantts/get-gantts.hook';
import { useGetAllMembers } from 'src/hooks/members/get-members.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetCustomersWithoutPagination } from 'src/hooks/customers/get-customers.hook';
import { useCheckIfProjectNumberAlreadyExists } from 'src/hooks/projects/check-if-project-number-exists';

import { filterOption } from './create-project-info.helpers';
import { FormContainer, ProjectAddressWrapper } from './create-project-info.styles';

const CreateProjectInfo = ({
  update,
  form,
  onSelectGantts,
  deputyMemberId,
  projectNumber,
}: {
  update?: boolean;
  form: FormInstance<any>;
  deputyMemberId?: string;
  projectNumber?: string;
  onSelectGantts: (values: any) => void;
}) => {
  const { t, i18n } = useTranslation(['common', 'projects', 'customers']);
  const { id } = useParams();
  const [enteredProjectNumber, setProjectNumber] = useState<string>();
  const { projectNumberExists } = useCheckIfProjectNumberAlreadyExists(enteredProjectNumber);

  const currentCompanyId = useGetCurrentCompanyId();
  const { gantts } = useGetGantts();
  const { ganttsAccess = [] } = useCurrentMemberContext();
  const { members } = useGetAllMembers({ companyId: currentCompanyId });
  const { customers } = useGetCustomersWithoutPagination({
    companyId: currentCompanyId,
    ...(!update && { deleted: false }),
  });
  const filteredMembers = members.filter(
    ({ companyPosition }: any) => companyPosition && companyPosition.name === 'Project Owner'
  );

  useEffect(() => {
    const member = filteredMembers.find((item: any) => item._id === deputyMemberId);
    form.setFieldsValue({ deputyMemberId: member ? member._id : undefined });
  }, [filteredMembers, form, deputyMemberId]);

  useEffect(() => {
    if (isBoolean(projectNumberExists))
      form.setFields([
        {
          name: 'projectNumber',
          errors: projectNumberExists ? [t('projects:labels.projectNumberExists')] : [],
        },
      ]);
  }, [projectNumberExists, form, t]);

  return (
    <FormContainer>
      <div>
        <Form.Item
          name="name"
          label={t('projects:labels.projectName')}
          rules={[{ required: true, message: t('projects:validation.projectNameRequired') }]}
        >
          <TextInput maxLength={50} placeholder="Bill" />
        </Form.Item>

        <Form.Item name="projectNumber" label={t('projects:labels.projectNumber')}>
          <TextInput
            placeholder="00"
            maxLength={30}
            onChange={event => {
              const newNumber = event.currentTarget.value;
              const isCurrentNumber = update && projectNumber === newNumber;

              if (!isCurrentNumber) {
                setProjectNumber(newNumber);
              }
            }}
          />
        </Form.Item>

        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const tasks = getFieldValue('tasks') || [];
            const totalHours = tasks.reduce(
              (memo: number, { timeStripes }: any) =>
                memo + (sumBy(timeStripes, 'plannedHours') || 0),
              0
            );
            const placeholder = totalHours
              ? `${round(totalHours, 2)} hr`
              : t('projects:labels.plannedHoursPlaceholder');

            return (
              <Form.Item
                required
                name="projectPlannedHours"
                label={t('projects:labels.projectPlannedHours')}
              >
                <HoursInput disabled placeholder={placeholder} />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item
          name="startDate"
          label={t('projects:labels.projectStartDate')}
          rules={[{ required: true, message: t('projects:validation.startDateRequired') }]}
        >
          <DatePickerInput lang={i18n.language} />
        </Form.Item>

        <Form.Item name="details" label={t('projects:labels.projectDetails')}>
          <TextAreaInput
            maxLength={500}
            showCount
            placeholder={t('projects:labels.writeDetails')}
          />
        </Form.Item>
      </div>
      <div>
        <Form.Item
          name="ganttIds"
          label={t('common:labels.gantt')}
          rules={[{ required: true, message: t('common:validation.error.ganttRequired') }]}
        >
          <CheckboxSelect
            onChange={onSelectGantts}
            showSearch
            filterOption={filterOption}
            placeholder={t('projects:task.selectNeededGantt')}
            optionData={ganttsAccess.map(({ ganttId }: any) => {
              const { name } = gantts.find(({ _id }: any) => _id === ganttId) || {};

              return { value: ganttId, label: name };
            })}
          />
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const customerId = getFieldValue('customerId');
            const options = customers.map((doc: any) => ({
              _id: doc._id,
              name:
                doc._id === customerId && doc.deleted
                  ? t('customers:labels.customerNameDeleted', { name: doc.name })
                  : doc.name,
            }));

            return (
              <Form.Item
                name="customerId"
                label={t('customers:labels.customerName')}
                rules={[{ required: true, message: t('customers:error.customerNameRequired') }]}
              >
                <SelectInput
                  disabled={update || Boolean(id)}
                  placeholder={t('projects:labels.startTyping')}
                  showArrow={false}
                  showSearch
                  filterOption={filterOption}
                >
                  {options.map((customer: any) => (
                    <SelectInput.Option key={customer._id} value={customer._id}>
                      {customer.name}
                    </SelectInput.Option>
                  ))}
                </SelectInput>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const currentMember = members.find(
              (item: any) => item._id === getFieldValue('ownerMemberId')
            );

            return (
              <Form.Item
                name="ownerMemberId"
                label={t('projects:labels.projectOwner')}
                rules={[{ required: true, message: t('projects:validation.projectOwnerRequired') }]}
              >
                <SelectInput placeholder="Guy Hawkins" showSearch filterOption={filterOption}>
                  {filteredMembers.map((member: any) => (
                    <SelectInput.Option
                      key={member._id}
                      value={member._id}
                      disabled={member._id === getFieldValue('deputyMemberId')}
                    >
                      {`${member.profile.firstName} ${member.profile.lastName}`}
                    </SelectInput.Option>
                  ))}
                  {update && currentMember && (
                    <SelectInput.Option
                      hidden
                      value={currentMember._id}
                    >{`${currentMember.profile.firstName} ${currentMember.profile.lastName}`}</SelectInput.Option>
                  )}
                </SelectInput>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <Form.Item name="deputyMemberId" label={t('projects:labels.projectOwnerDeputy')}>
              <SelectInput placeholder="Kristin Watson" showSearch filterOption={filterOption}>
                {filteredMembers.map((member: any) => (
                  <SelectInput.Option
                    key={member._id}
                    value={member._id}
                    disabled={member._id === getFieldValue('ownerMemberId')}
                  >
                    {member.profile.firstName} {member.profile.lastName}
                  </SelectInput.Option>
                ))}
              </SelectInput>
            </Form.Item>
          )}
        </Form.Item>
      </div>
      <ProjectAddressWrapper>
        <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
          {t('projects:labels.projectAddress')}
        </Typography>
        <AddressInput t={t} parentField="address" notRequired />
      </ProjectAddressWrapper>
    </FormContainer>
  );
};

export default CreateProjectInfo;
